import React, { useEffect, useMemo, useState } from 'react';

import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  PaginationState,
  RowSelectionState,
  SortingState,
  Table as ReactTable,
  useReactTable,
  Row,
  VisibilityState,
} from '@tanstack/react-table';

import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DataTablePagination } from './DataTablePagination';
import { DataTableNumberedPagination } from './DataTableNumberedPagination';
import { DataTableToolbar } from './DataTableToolbar';
import { Skeleton } from '@/components/ui/skeleton';
import { DataTableFacetedFilterProps } from './DataTableFacetedFilter';
import { ApolloError } from '@apollo/client';
import { useIsMobile } from '@/hooks/use-mobile';
import i18n from '@/utils/i18n';
import { DataTableRowCards } from './DataTableRowCards';
import { createSelectColumn } from './DataTableSelectColumn';

const LOADING_SKELETON_ROW_COUNT = 5;
const LOADING_SKELETON_HEIGHT = '1.33em';

const DEFAULT_PAGE_SIZE = 10;

type MobileRowRepresentation<TData> =
  | {
      type: 'table';
    }
  | {
      type: 'cards';
      /** The id of the column whose value will be used as the title of the card */
      cardTitleColumnId: string;
    }
  | {
      type: 'custom';
      /** Function to render rows in custom mobile view */
      renderMobileRow: (data: TData, row: Row<TData>) => React.ReactNode;
    };

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  error?: ApolloError | string | null;
  /** Server-side pagination state */
  pagination?: {
    rowCount: number;
    state: PaginationState;
    setState: OnChangeFn<PaginationState>;
  };
  /** Controlled row selection (optional; enables bulk actions in parent). */
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  opts?: {
    /** For client-side pagination (when no pagination prop is provided) */
    pageSize?: number;
    availablePageSizes?: number[];
    /**
     * Pagination UI style. Defaults to 'rows-per-page' (rows-per-page
     * dropdown + "Page X / Y" + arrow buttons). Use 'numbered' for the
     * "Previous 1 2 … N Next" numbered-page style.
     */
    paginationVariant?: 'rows-per-page' | 'numbered';
    loadingSkeletonHeight?: string;
    loadingSkeletonRowCount?: number;
    facetedFilters?: Array<
      Omit<DataTableFacetedFilterProps<TData, any>, 'column'> & {
        columnId: string;
      }
    >;
    /** Component to render in the right side of the toolbar */
    ToolbarRightComponent?: React.ReactNode;
    mobileRowRepresentation?: MobileRowRepresentation<TData>;
    enableRowSelection?: boolean;
    getRowId?: (row: TData) => string;
    /** When true with row selection, show selected count in pagination footer. */
    showSelectedRowCountInPagination?: boolean;
    /** Rendered inside the same `<table>` after `<tbody>` (desktop layout only). */
    tableFooter?: (ctx: { table: ReactTable<TData> }) => React.ReactNode;
    /** Initial column visibility state (e.g. `{ currency: false }` to hide a filter-only column). */
    initialColumnVisibility?: VisibilityState;
    /** Trigger style for the column-visibility menu. Defaults to a "View" button. */
    viewOptionsVariant?: 'button' | 'icon';
  };
}

export function DataTable<TData, TValue>({
  columns,
  data,
  isLoading,
  error,
  pagination,
  rowSelection: rowSelectionControlled,
  onRowSelectionChange: onRowSelectionChangeControlled,
  opts = {},
}: DataTableProps<TData, TValue>) {
  const [clientPagination, setClientPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: opts.pageSize ?? DEFAULT_PAGE_SIZE,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    opts.initialColumnVisibility ?? {},
  );
  const [sorting, setSorting] = useState<SortingState>([]);
  const [internalRowSelection, setInternalRowSelection] =
    useState<RowSelectionState>({});
  const isMobile = useIsMobile();

  const enableRowSelection = !!opts.enableRowSelection;
  const isControlledSelection =
    rowSelectionControlled !== undefined &&
    onRowSelectionChangeControlled !== undefined;
  const rowSelection = isControlledSelection
    ? rowSelectionControlled
    : internalRowSelection;
  const onRowSelectionChange = isControlledSelection
    ? onRowSelectionChangeControlled
    : setInternalRowSelection;

  const loadingSkeletonRowCount =
    opts.loadingSkeletonRowCount ?? LOADING_SKELETON_ROW_COUNT;
  const loadingSkeletonHeight =
    opts.loadingSkeletonHeight ?? LOADING_SKELETON_HEIGHT;

  const tableColumns = useMemo(() => {
    if (!enableRowSelection) {
      return columns;
    }
    return [createSelectColumn<TData>(), ...columns];
  }, [columns, enableRowSelection]);

  useEffect(() => {
    if (!enableRowSelection) {
      return;
    }
    if (!isControlledSelection) {
      setInternalRowSelection({});
    }
  }, [data, enableRowSelection, isControlledSelection]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      pagination: !pagination ? clientPagination : pagination.state,
      rowSelection: enableRowSelection ? rowSelection : {},
    },
    rowCount: pagination?.rowCount,
    manualPagination: !!pagination,
    enableRowSelection,
    getRowId: enableRowSelection
      ? (row) => opts.getRowId?.(row) ?? ''
      : undefined,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: pagination?.setState ?? setClientPagination,
    onRowSelectionChange: enableRowSelection ? onRowSelectionChange : undefined,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  const tableHasRows = table.getRowModel().rows?.length > 0;

  const selectedCount = enableRowSelection
    ? Object.keys(rowSelection).filter((id) => rowSelection[id]).length
    : 0;

  const selectAllAcrossPages =
    enableRowSelection && opts.getRowId
      ? () => {
          const next: RowSelectionState = {};
          for (const row of data) {
            next[opts.getRowId!(row)] = true;
          }
          onRowSelectionChange(next);
        }
      : undefined;

  const selectionToolbar =
    enableRowSelection && selectAllAcrossPages && data.length > 0
      ? {
          selectedCount,
          totalRowCount: data.length,
          onSelectAllAcrossPages: selectAllAcrossPages,
        }
      : null;

  const showPaginationSelection =
    !!opts.showSelectedRowCountInPagination && enableRowSelection;

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <DataTableToolbar
        table={table}
        isLoading={false}
        facetedFilters={opts.facetedFilters}
        RightComponent={opts.ToolbarRightComponent}
        selectionToolbar={selectionToolbar}
        viewOptionsVariant={opts.viewOptionsVariant}
      />
      {isMobile &&
      opts.mobileRowRepresentation?.type !== 'table' &&
      opts.mobileRowRepresentation != null ? (
        <div className="tw:flex tw:flex-col tw:gap-4">
          <TableBodyContent
            isLoading={!!isLoading}
            error={error}
            tableHasRows={tableHasRows}
            table={table}
            columns={tableColumns}
            loadingSkeletonRowCount={loadingSkeletonRowCount}
            loadingSkeletonHeight={loadingSkeletonHeight}
            mobileRowRepresentation={opts.mobileRowRepresentation}
          />
        </div>
      ) : (
        <div className="tw:overflow-x-auto tw:rounded-md tw:border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id} colSpan={header.colSpan}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              <TableBodyContent
                isLoading={!!isLoading}
                error={error}
                tableHasRows={tableHasRows}
                table={table}
                columns={tableColumns}
                loadingSkeletonRowCount={loadingSkeletonRowCount}
                loadingSkeletonHeight={loadingSkeletonHeight}
              />
            </TableBody>
            {opts.tableFooter && !isLoading && !error && tableHasRows ? (
              <TableFooter>{opts.tableFooter({ table })}</TableFooter>
            ) : null}
          </Table>
        </div>
      )}
      {opts.paginationVariant === 'numbered' ? (
        <DataTableNumberedPagination table={table} />
      ) : (
        <DataTablePagination
          table={table}
          availablePageSizes={opts.availablePageSizes}
          showSelectedRowCount={showPaginationSelection}
        />
      )}
    </div>
  );
}

interface TableBodyContentProps<TData, TValue> {
  isLoading: boolean;
  error?: ApolloError | string | null;
  tableHasRows: boolean;
  table: ReactTable<TData>;
  columns: ColumnDef<TData, TValue>[];
  loadingSkeletonRowCount: number;
  loadingSkeletonHeight: string;
  mobileRowRepresentation?: MobileRowRepresentation<TData>;
}

function TableBodyContent<TData, TValue>({
  isLoading,
  error,
  tableHasRows,
  table,
  columns,
  loadingSkeletonRowCount,
  loadingSkeletonHeight,
  mobileRowRepresentation,
}: TableBodyContentProps<TData, TValue>) {
  const isMobile = useIsMobile();

  if (error) {
    const errorMessage = error instanceof ApolloError ? error.message : error;
    if (isMobile) {
      return (
        <div className="tw:h-24 tw:flex tw:items-center tw:justify-center tw:text-center tw:p-4">
          {errorMessage}
        </div>
      );
    }
    return (
      <TableRow>
        <TableCell colSpan={columns.length} className="tw:h-24 tw:text-center">
          {errorMessage}
        </TableCell>
      </TableRow>
    );
  }

  if (isLoading) {
    if (isMobile) {
      return (
        <>
          {Array(loadingSkeletonRowCount)
            .fill(null)
            .map((_, index) => (
              <div
                key={`mobile-skeleton-${index}`}
                className="tw:p-4 tw:border tw:rounded-lg tw:mb-4"
              >
                <Skeleton
                  className={`tw:mb-2`}
                  style={{ height: loadingSkeletonHeight }}
                />
                <Skeleton
                  className={`tw:w-3/4`}
                  style={{ height: loadingSkeletonHeight }}
                />
              </div>
            ))}
        </>
      );
    }
    return (
      <>
        {Array(loadingSkeletonRowCount)
          .fill(null)
          .map((_, index) => (
            <TableRow key={`skeleton-${index}`}>
              {columns.map((_, colIndex) => (
                <TableCell key={`skeleton-cell-${colIndex}`}>
                  <Skeleton style={{ height: loadingSkeletonHeight }} />
                </TableCell>
              ))}
            </TableRow>
          ))}
      </>
    );
  }

  if (!tableHasRows) {
    if (isMobile) {
      return (
        <div className="tw:h-24 tw:flex tw:items-center tw:justify-center tw:text-center tw:p-4">
          {i18n.t('ItemLocation:noResults')}
        </div>
      );
    }

    return (
      <TableRow>
        <TableCell colSpan={columns.length} className="tw:h-24 tw:text-center">
          {i18n.t('ItemLocation:noResults')}
        </TableCell>
      </TableRow>
    );
  }

  if (isMobile) {
    switch (mobileRowRepresentation?.type) {
      case 'cards': {
        return (
          <DataTableRowCards
            table={table}
            cardTitleColumnId={mobileRowRepresentation.cardTitleColumnId}
          />
        );
      }
      case 'custom': {
        if (!mobileRowRepresentation?.renderMobileRow) {
          throw new Error(
            'renderMobileRow is required when mobileRowRepresentation is custom',
          );
        }
        return (
          <>
            {table.getRowModel().rows.map((row) => (
              <React.Fragment key={row.id}>
                {mobileRowRepresentation.renderMobileRow(row.original, row)}
              </React.Fragment>
            ))}
          </>
        );
      }
      case 'table':
      default:
        break;
    }
  }
  return (
    <>
      {table.getRowModel().rows.map((row) => (
        <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
          {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}
